import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaChartLine, 
  FaWallet, 
  FaUser, 
  FaCog, 
  FaSignOutAlt, 
  FaBell, 
  FaSearch, 
  FaStar, 
  FaExchangeAlt,
  FaArrowUp,
  FaArrowDown,
  FaBitcoin,
  FaEthereum,
  FaDollarSign,
  FaUsers,
  FaHeadset
} from 'react-icons/fa';

const SimpleDashboard = () => {
  const [user, setUser] = useState({
    name: "John Doe",
    email: "john.doe@example.com",
    membership: "Premium",
    balance: 12500.75,
    profit: 2450.30
  });

  const [activeTab, setActiveTab] = useState('overview');
  const [notifications, setNotifications] = useState([
    { id: 1, message: "Your withdrawal request has been processed", time: "2 hours ago", read: false },
    { id: 2, message: "New broker added: Global Trade Markets", time: "1 day ago", read: true },
    { id: 3, message: "Market update: Bitcoin reaches new high", time: "2 days ago", read: true }
  ]);

  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is authenticated
    // In a real app, this would check for a valid session/token
    const isAuthenticated = true; // Simulated authentication
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [navigate]);

  const unreadNotifications = notifications.filter(n => !n.read).length;

  const markAsRead = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? {...n, read: true} : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({...n, read: true})));
  };

  const handleLogout = () => {
    // In a real app, this would clear the session/token
    alert('You have been logged out');
    navigate('/login');
  };

  const favoriteBrokers = [
    { id: 1, name: "Global Trade Markets", rating: 4.8, regulation: "FCA, CySEC", instantWithdrawal: true },
    { id: 2, name: "CryptoCap Brokers", rating: 4.6, regulation: "ASIC, FMA", instantWithdrawal: true }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  const staggerChildren = {
    visible: { 
      transition: { 
        staggerChildren: 0.1 
      } 
    }
  };

  const floatAnimation = {
    y: [0, -5, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div className="dashboard">
      {/* Top Navigation Bar */}
      <motion.div 
        className="dashboard-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div 
          className="dashboard-logo"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Link to="/">UPM Traders</Link>
        </motion.div>
        
        <div className="dashboard-search">
          <div className="search-input">
            <FaSearch />
            <input type="text" placeholder="Search brokers, markets..." />
          </div>
        </div>
        
        <div className="dashboard-actions">
          <motion.div 
            className="notification-bell"
            whileHover={{ scale: 1.1 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <FaBell />
            {unreadNotifications > 0 && (
              <motion.span 
                className="notification-badge"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500 }}
              >
                {unreadNotifications}
              </motion.span>
            )}
          </motion.div>
          
          <motion.div 
            className="user-profile"
            whileHover={{ x: 5 }}
            transition={{ duration: 0.3 }}
          >
            <div className="user-avatar">
              <FaUser />
            </div>
            <div className="user-info">
              <motion.span 
                className="user-name"
                animate={{ 
                  color: ["#B0BEC5", "#D4AF37", "#B0BEC5"]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                {user.name}
              </motion.span>
              <span className="user-membership">{user.membership} Member</span>
            </div>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Main Content */}
      <div className="dashboard-content">
        {/* Sidebar */}
        <motion.div 
          className="dashboard-sidebar"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <nav className="sidebar-nav">
            <motion.button
              className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
              whileHover={{ 
                x: 5,
                backgroundColor: "#121F38",
                borderColor: "#D4AF37",
                transition: { duration: 0.3 }
              }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <motion.span 
                className="nav-icon"
                animate={activeTab === 'overview' ? { 
                  color: "#D4AF37",
                  scale: 1.1
                } : {}}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FaChartLine />
              </motion.span>
              <motion.span 
                className="nav-label"
                animate={activeTab === 'overview' ? { 
                  color: "#D4AF37",
                  fontWeight: "bold"
                } : {}}
                transition={{ duration: 0.3 }}
              >
                Overview
              </motion.span>
            </motion.button>
            
            <motion.button
              className={`nav-item ${activeTab === 'portfolio' ? 'active' : ''}`}
              onClick={() => setActiveTab('portfolio')}
              whileHover={{ 
                x: 5,
                backgroundColor: "#121F38",
                borderColor: "#D4AF37",
                transition: { duration: 0.3 }
              }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="nav-icon"><FaWallet /></span>
              <span className="nav-label">Portfolio</span>
            </motion.button>
            
            <motion.button
              className={`nav-item ${activeTab === 'brokers' ? 'active' : ''}`}
              onClick={() => setActiveTab('brokers')}
              whileHover={{ 
                x: 5,
                backgroundColor: "#121F38",
                borderColor: "#D4AF37",
                transition: { duration: 0.3 }
              }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="nav-icon"><FaSearch /></span>
              <span className="nav-label">Brokers</span>
            </motion.button>
          </nav>
          
          <div className="sidebar-footer">
            <motion.button 
              className="nav-item"
              whileHover={{ 
                x: 5,
                backgroundColor: "#121F38",
                borderColor: "#D4AF37",
                transition: { duration: 0.3 }
              }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="nav-icon"><FaHeadset /></span>
              <span className="nav-label">Support</span>
            </motion.button>
            <motion.button 
              className="nav-item" 
              onClick={handleLogout}
              whileHover={{ 
                x: 5,
                backgroundColor: "#121F38",
                borderColor: "#D4AF37",
                transition: { duration: 0.3 }
              }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="nav-icon"><FaSignOutAlt /></span>
              <span className="nav-label">Logout</span>
            </motion.button>
          </div>
        </motion.div>
        
        {/* Main Dashboard Area */}
        <div className="dashboard-main">
          <div className="dashboard-section">
            {/* Welcome Section */}
            <motion.div 
              className="welcome-section"
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <motion.h1
                animate={floatAnimation}
              >
                Welcome back, {user.name.split(' ')[0]}!
              </motion.h1>
              <p>Here's what's happening with your account today.</p>
            </motion.div>
            
            {/* Stats Cards - Simplified to just the key elements */}
            <motion.div 
              className="stats-grid"
              initial="hidden"
              animate="visible"
              variants={staggerChildren}
            >
              <motion.div 
                className="stat-card" 
                variants={fadeInUp}
                whileHover={{ 
                  y: -10,
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
                  borderColor: "#D4AF37",
                  transition: { duration: 0.3 }
                }}
              >
                <div className="stat-header">
                  <h3>Account Balance</h3>
                  <motion.div 
                    className="stat-icon"
                    animate={floatAnimation}
                  >
                    <FaWallet />
                  </motion.div>
                </div>
                <div className="stat-value">${user.balance.toLocaleString()}</div>
                <div className="stat-change positive">+2.5% this month</div>
              </motion.div>
              
              <motion.div 
                className="stat-card" 
                variants={fadeInUp}
                whileHover={{ 
                  y: -10,
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
                  borderColor: "#D4AF37",
                  transition: { duration: 0.3 }
                }}
              >
                <div className="stat-header">
                  <h3>Total Profit</h3>
                  <motion.div 
                    className="stat-icon"
                    animate={floatAnimation}
                    transition={{ delay: 0.2 }}
                  >
                    <FaChartLine />
                  </motion.div>
                </div>
                <div className="stat-value">${user.profit.toLocaleString()}</div>
                <div className="stat-change positive">+12.3% this year</div>
              </motion.div>
              
              <motion.div 
                className="stat-card" 
                variants={fadeInUp}
                whileHover={{ 
                  y: -10,
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
                  borderColor: "#D4AF37",
                  transition: { duration: 0.3 }
                }}
              >
                <div className="stat-header">
                  <h3>Favorite Brokers</h3>
                  <motion.div 
                    className="stat-icon"
                    animate={floatAnimation}
                    transition={{ delay: 0.4 }}
                  >
                    <FaStar />
                  </motion.div>
                </div>
                <div className="stat-value">{favoriteBrokers.length}</div>
                <div className="stat-change">Tracked brokers</div>
              </motion.div>
            </motion.div>
            
            {/* Favorite Brokers Section */}
            <motion.div 
              className="brokers-section"
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <div className="section-header">
                <h2>Your Favorite Brokers</h2>
                <motion.button 
                  className="btn btn-outline"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveTab('brokers')}
                >
                  View All Brokers
                </motion.button>
              </div>
              
              <div className="brokers-grid">
                {favoriteBrokers.map((broker, index) => (
                  <motion.div 
                    className="broker-card" 
                    key={broker.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                    whileHover={{ 
                      y: -10,
                      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
                      borderColor: "#D4AF37",
                      transition: { duration: 0.3 }
                    }}
                  >
                    <div className="broker-header">
                      <h3>{broker.name}</h3>
                      <div className="broker-rating">
                        <FaStar />
                        <span>{broker.rating}</span>
                      </div>
                    </div>
                    <div className="broker-info">
                      <p>Regulated by: {broker.regulation}</p>
                      <div className="broker-features">
                        {broker.instantWithdrawal && (
                          <span className="feature-tag">Instant Withdrawal</span>
                        )}
                        <span className="feature-tag">Low Fees</span>
                      </div>
                    </div>
                    <div className="broker-actions">
                      <motion.button 
                        className="btn btn-brand"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Trade Now
                      </motion.button>
                      <motion.button 
                        className="btn btn-outline"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setActiveTab('brokers')}
                      >
                        View Details
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleDashboard;